import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, X, Plus, Trash2, Clock, ArrowRight, Share2 } from "lucide-react";
import { DEAL_STAGES, STAGE_DOT_COLORS, STAGE_LABELS, formatDealSize } from "@/lib/deal-stages";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Deal {
  id: string;
  name: string;
  stage: string;
  asset_class: string | null;
  size: number | null;
  contact_id: string | null;
  notes: string | null;
  next_steps: string | null;
  created_at: string;
  updated_at: string;
}

interface Contact {
  id: string;
  full_name: string;
  email: string | null;
  organization: string | null;
}

interface Meeting {
  id: string;
  title: string | null;
  start_at: string | null;
}

interface Activity {
  id: string;
  type: string;
  date: string;
  summary: string | null;
}

interface Family {
  id: string;
  name: string;
}

interface DealShare {
  id: string;
  target_family_id: string;
  status: string;
  proposed_at: string;
  families?: { name: string } | null;
}

interface DealDetailDrawerProps {
  dealId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
  orgId?: string;
  isMFO?: boolean;
  familyId?: string;
  families?: Family[];
}

export function DealDetailDrawer({ dealId, open, onOpenChange, onUpdate, orgId, isMFO, familyId, families = [] }: DealDetailDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deal, setDeal] = useState<Deal | null>(null);
  const [draft, setDraft] = useState<Partial<Deal>>({});
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [linkedMeetings, setLinkedMeetings] = useState<Meeting[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string>("");
  const [dealShares, setDealShares] = useState<DealShare[]>([]);
  const [selectedShareFamilyId, setSelectedShareFamilyId] = useState<string>("");

  const isDirty = deal && (
    draft.name !== deal.name ||
    draft.stage !== deal.stage ||
    draft.asset_class !== deal.asset_class ||
    draft.size !== deal.size ||
    draft.contact_id !== deal.contact_id ||
    draft.notes !== deal.notes ||
    draft.next_steps !== deal.next_steps
  );

  const resetDraft = useCallback((d: Deal) => {
    setDraft({
      name: d.name,
      stage: d.stage,
      asset_class: d.asset_class,
      size: d.size,
      contact_id: d.contact_id,
      notes: d.notes,
      next_steps: d.next_steps,
    });
  }, []);

  useEffect(() => {
    if (open && dealId) {
      loadDeal();
      loadContacts();
      loadMeetings();
      loadLinkedMeetings();
      loadActivities();
      if (isMFO) loadDealShares();
    }
  }, [open, dealId]);

  const loadDeal = async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("deals")
        .select("*")
        .eq("id", dealId)
        .single();

      if (error) throw error;
      const d = data as Deal;
      setDeal(d);
      resetDraft(d);
    } catch (error) {
      console.error("Error loading deal:", error);
      toast.error("Failed to load deal details");
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const { data, error } = await supabase
        .from("contacts")
        .select("id, full_name, email, organization")
        .order("full_name");

      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      console.error("Error loading contacts:", error);
    }
  };

  const loadMeetings = async () => {
    try {
      const { data, error } = await supabase
        .from("meetings")
        .select("id, title, start_at")
        .order("start_at", { ascending: false });

      if (error) throw error;
      setMeetings(data || []);
    } catch (error) {
      console.error("Error loading meetings:", error);
    }
  };

  const loadLinkedMeetings = async () => {
    if (!dealId) return;
    try {
      const { data, error } = await supabase
        .from("deals_meetings")
        .select(`
          meeting_id,
          meetings:meeting_id (
            id,
            title,
            start_at
          )
        `)
        .eq("deal_id", dealId);

      if (error) throw error;
      const meetingsList = data?.map(dm => dm.meetings).filter(Boolean) as Meeting[];
      setLinkedMeetings(meetingsList || []);
    } catch (error) {
      console.error("Error loading linked meetings:", error);
    }
  };

  const loadActivities = async () => {
    if (!dealId) return;
    try {
      const { data, error } = await supabase
        .from("activities")
        .select("id, type, date, summary")
        .eq("deal_id", dealId)
        .order("date", { ascending: false })
        .limit(20);

      if (error) throw error;
      setActivities(data || []);
    } catch (error) {
      console.error("Error loading activities:", error);
    }
  };

  const loadDealShares = async () => {
    if (!dealId) return;
    try {
      const { data, error } = await supabase
        .from("deal_sharing")
        .select("id, target_family_id, status, proposed_at, families:target_family_id(name)")
        .eq("deal_id", dealId)
        .order("proposed_at", { ascending: false });

      if (error) throw error;
      setDealShares((data || []) as DealShare[]);
    } catch (error) {
      console.error("Error loading deal shares:", error);
    }
  };

  const handleShareDeal = async () => {
    if (!dealId || !selectedShareFamilyId || !familyId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("deal_sharing")
        .insert({
          org_id: orgId,
          deal_id: dealId,
          source_family_id: familyId,
          target_family_id: selectedShareFamilyId,
          status: "proposed",
          proposed_by: user?.id,
        });

      if (error) throw error;

      toast.success("Deal shared with family");
      setSelectedShareFamilyId("");
      loadDealShares();
    } catch (err) {
      console.error("Error sharing deal:", err);
      const message = err instanceof Error && err.message?.includes("duplicate")
        ? "Deal already shared with this family"
        : "Failed to share deal";
      toast.error(message);
    }
  };

  const handleSave = async () => {
    if (!deal || !dealId || !isDirty) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("deals")
        .update({
          name: draft.name,
          stage: draft.stage,
          asset_class: draft.asset_class || null,
          size: draft.size,
          contact_id: draft.contact_id || null,
          notes: draft.notes || null,
          next_steps: draft.next_steps || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", dealId);

      if (error) throw error;

      const updated = { ...deal, ...draft, updated_at: new Date().toISOString() };
      setDeal(updated);
      resetDraft(updated);
      toast.success("Deal saved");
      onUpdate();
    } catch (error) {
      console.error("Error saving deal:", error);
      toast.error("Failed to save deal");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!dealId) return;

    setDeleting(true);
    try {
      const { error } = await supabase.from("deals").delete().eq("id", dealId);
      if (error) throw error;

      toast.success("Deal deleted");
      setDeleteConfirmOpen(false);
      onOpenChange(false);
      onUpdate();
    } catch (error) {
      console.error("Error deleting deal:", error);
      toast.error("Failed to delete deal");
    } finally {
      setDeleting(false);
    }
  };

  const handleLinkMeeting = async () => {
    if (!dealId || !selectedMeetingId) return;

    try {
      const { error } = await supabase
        .from("deals_meetings")
        .insert({
          deal_id: dealId,
          meeting_id: selectedMeetingId,
        });

      if (error) throw error;

      toast.success("Meeting linked to deal");
      setSelectedMeetingId("");
      loadLinkedMeetings();
    } catch (err) {
      console.error("Error linking meeting:", err);
      const message = err instanceof Error && err.message?.includes("duplicate")
        ? "Meeting already linked to this deal"
        : "Failed to link meeting";
      toast.error(message);
    }
  };

  const handleUnlinkMeeting = async (meetingId: string) => {
    if (!dealId) return;

    try {
      const { error } = await supabase
        .from("deals_meetings")
        .delete()
        .eq("deal_id", dealId)
        .eq("meeting_id", meetingId);

      if (error) throw error;

      toast.success("Meeting unlinked from deal");
      loadLinkedMeetings();
    } catch (error) {
      console.error("Error unlinking meeting:", error);
      toast.error("Failed to unlink meeting");
    }
  };

  const getStageColor = (stage: string) => STAGE_DOT_COLORS[stage] || "bg-gray-500";

  if (loading) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-2xl">
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (!deal) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-2xl p-0">
          <ScrollArea className="h-full">
            <div className="p-6 space-y-6">
              <SheetHeader className="flex flex-row items-start justify-between gap-4">
                <div className="flex-1">
                  <SheetTitle className="text-2xl">{deal.name}</SheetTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      <div className={`w-2 h-2 rounded-full mr-1 ${getStageColor(deal.stage)}`} />
                      {STAGE_LABELS[deal.stage] || deal.stage}
                    </Badge>
                    {deal.size && (
                      <span className="text-sm text-muted-foreground">{formatDealSize(deal.size)}</span>
                    )}
                  </div>
                </div>
              </SheetHeader>

              <div className="space-y-4">
                {/* Deal Name */}
                <div className="space-y-2">
                  <Label>Deal Name</Label>
                  <Input
                    value={draft.name ?? ""}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Deal name"
                  />
                </div>

                {/* Stage */}
                <div className="space-y-2">
                  <Label>Stage</Label>
                  <Select
                    value={draft.stage}
                    onValueChange={(value) => setDraft({ ...draft, stage: value })}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${getStageColor(draft.stage || "")}`} />
                          {STAGE_LABELS[draft.stage || ""] || draft.stage}
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {DEAL_STAGES.map((stage) => (
                        <SelectItem key={stage.value} value={stage.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${getStageColor(stage.value)}`} />
                            {stage.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Asset Class */}
                  <div className="space-y-2">
                    <Label>Asset Class</Label>
                    <Select
                      value={draft.asset_class || undefined}
                      onValueChange={(value) => setDraft({ ...draft, asset_class: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select asset class" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Private Equity">Private Equity</SelectItem>
                        <SelectItem value="Venture Capital">Venture Capital</SelectItem>
                        <SelectItem value="Real Estate">Real Estate</SelectItem>
                        <SelectItem value="Credit">Credit</SelectItem>
                        <SelectItem value="Infrastructure">Infrastructure</SelectItem>
                        <SelectItem value="Hedge Fund">Hedge Fund</SelectItem>
                        <SelectItem value="Public Equity">Public Equity</SelectItem>
                        <SelectItem value="Fixed Income">Fixed Income</SelectItem>
                        <SelectItem value="Co-Investment">Co-Investment</SelectItem>
                        <SelectItem value="Direct Investment">Direct Investment</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Size */}
                  <div className="space-y-2">
                    <Label>Size ($)</Label>
                    <Input
                      type="number"
                      value={draft.size ?? ""}
                      onChange={(e) => setDraft({ ...draft, size: e.target.value ? parseFloat(e.target.value) : null })}
                      placeholder="Deal size"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Contact */}
                  <div className="space-y-2">
                    <Label>Contact</Label>
                    <Select
                      value={draft.contact_id || undefined}
                      onValueChange={(value) => setDraft({ ...draft, contact_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select contact" />
                      </SelectTrigger>
                      <SelectContent>
                        {contacts.map((contact) => (
                          <SelectItem key={contact.id} value={contact.id}>
                            {contact.full_name} {contact.organization && `(${contact.organization})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={draft.notes ?? ""}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    placeholder="Add deal notes, investment thesis..."
                    rows={5}
                    className="resize-none"
                  />
                </div>

                {/* Next Steps */}
                <div className="space-y-2">
                  <Label>Next Steps</Label>
                  <Textarea
                    value={draft.next_steps ?? ""}
                    onChange={(e) => setDraft({ ...draft, next_steps: e.target.value })}
                    placeholder="What needs to happen next..."
                    rows={3}
                    className="resize-none"
                  />
                </div>

                {/* Save / Delete actions */}
                <div className="flex items-center gap-2 pt-2">
                  <Button onClick={handleSave} disabled={!isDirty || saving}>
                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                    Save Changes
                  </Button>
                  {isDirty && (
                    <Button variant="ghost" onClick={() => resetDraft(deal)}>
                      Discard
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Linked Meetings */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Related Meetings</Label>

                {linkedMeetings.length > 0 && (
                  <div className="space-y-2">
                    {linkedMeetings.map((meeting) => (
                      <div
                        key={meeting.id}
                        className="flex items-center justify-between p-3 border rounded-lg bg-muted/30"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-sm">{meeting.title || "Untitled Meeting"}</div>
                          {meeting.start_at && (
                            <div className="text-xs text-muted-foreground">
                              {new Date(meeting.start_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnlinkMeeting(meeting.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Select value={selectedMeetingId} onValueChange={setSelectedMeetingId}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select meeting to link" />
                    </SelectTrigger>
                    <SelectContent>
                      {meetings
                        .filter(m => !linkedMeetings.find(lm => lm.id === m.id))
                        .map((meeting) => (
                          <SelectItem key={meeting.id} value={meeting.id}>
                            {meeting.title || "Untitled Meeting"}
                            {meeting.start_at && ` - ${new Date(meeting.start_at).toLocaleDateString()}`}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleLinkMeeting}
                    disabled={!selectedMeetingId}
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Link
                  </Button>
                </div>
              </div>

              {/* Activity Timeline */}
              {activities.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Activity</Label>
                    <div className="space-y-3">
                      {activities.map((activity) => (
                        <div key={activity.id} className="flex gap-3 text-sm">
                          <div className="flex flex-col items-center">
                            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                              {activity.type === "Meeting" ? (
                                <Clock className="h-3 w-3 text-muted-foreground" />
                              ) : (
                                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                              )}
                            </div>
                            <div className="w-px h-full bg-border" />
                          </div>
                          <div className="pb-3">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{activity.type}</Badge>
                              <span className="text-xs text-muted-foreground">
                                {new Date(activity.date).toLocaleDateString()}
                              </span>
                            </div>
                            {activity.summary && (
                              <p className="text-muted-foreground mt-1">{activity.summary}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Deal Sharing (MFO only) */}
              {isMFO && familyId && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Share2 className="h-4 w-4" />
                      Share with Families
                    </Label>

                    {dealShares.length > 0 && (
                      <div className="space-y-2">
                        {dealShares.map((share) => (
                          <div
                            key={share.id}
                            className="flex items-center justify-between p-2 border rounded-lg bg-muted/30"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">
                                {share.families?.name || "Unknown"}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {share.status}
                              </Badge>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(share.proposed_at).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Select value={selectedShareFamilyId} onValueChange={setSelectedShareFamilyId}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select family" />
                        </SelectTrigger>
                        <SelectContent>
                          {families
                            .filter(f => f.id !== familyId && !dealShares.find(s => s.target_family_id === f.id))
                            .map((family) => (
                              <SelectItem key={family.id} value={family.id}>
                                {family.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={handleShareDeal}
                        disabled={!selectedShareFamilyId}
                        size="sm"
                      >
                        <Share2 className="h-4 w-4 mr-1" />
                        Share
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {/* Metadata */}
              <div className="pt-4 border-t text-xs text-muted-foreground space-y-1">
                <div>Created: {new Date(deal.created_at).toLocaleString()}</div>
                <div>Last updated: {new Date(deal.updated_at).toLocaleString()}</div>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deal.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Delete Deal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
